/**
 * useOfficeIdentity - the office/client boundary, read from the live session.
 *
 * `config/officeIdentity.ts` holds the RULES and stays pure so they can be
 * tested. This hook is the one place that feeds it the store, so a surface asks
 * "am I a guest here?" rather than re-deriving it from `orgs` and `currentOrg`
 * on its own. Re-deriving is exactly how the boundary drifted the first time:
 * four surfaces each had their own idea of what a practice was, and the loosest
 * one decided what a client could see.
 *
 * @since office-guest (2026-08-10)
 */
import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { myOfficeOrg, myPracticeMembership, isVisitingClientBooks } from '../config/officeIdentity';
import { clientBooksName } from '../config/myBusinesses';
import type { Org } from '@/types/db';

export interface OfficeIdentity {
    /** The practice this person owns, or null if they run no firm. */
    officeOrg: Org | null;
    /** True while standing in somebody else's books on behalf of that firm. */
    visiting: boolean;
    /**
     * The host's name while visiting, else null. Components want the name far
     * more often than the boolean, because a boundary that does not say WHOSE
     * books you are in reads as a bug rather than as a rule.
     */
    hostName: string | null;
    /**
     * The client's name in ANY client's books, else null: `hostName`, plus a
     * client company the office owns because it created it. The phone's private
     * apps follow this one, so they stay the office's (waoffice).
     */
    clientName: string | null;
    /**
     * The office whose WhatsApp, mail and referrals this person's Karr Phone
     * carries: the one they own, else the one they work at, else null. Wider
     * than `officeOrg` on purpose: a team member is not an owner, and still
     * answers the office's WhatsApp wherever they stand (founder, 7 Oct 2026:
     * "the consultant or his teammates should always see his own whatsapp and
     * should have no access to clients whatsapp, whether in office or in
     * clients account").
     */
    phoneOffice: Org | null;
}

export function useOfficeIdentity(): OfficeIdentity {
    const orgs = useAuth((s) => s.orgs);
    const currentOrg = useAuth((s) => s.currentOrg);
    return useMemo(() => {
        const officeOrg = myOfficeOrg(orgs);
        const visiting = isVisitingClientBooks(currentOrg?.id, orgs);
        return {
            officeOrg,
            visiting,
            hostName: visiting ? (currentOrg?.name ?? 'this client') : null,
            clientName: clientBooksName(currentOrg?.id, orgs),
            phoneOffice: myPracticeMembership(orgs),
        };
    }, [orgs, currentOrg]);
}
